import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSaveDraftContent } from './useSaveDraftContent';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../context';
import { useRouter } from 'next/router';
import { CreateVideoSideMenuItemEnum } from '../../utils';

interface Props {
  draftName: string;
  draftAvatarId: string;
  draftAudioId: string;
  draftContent: string;
  draftId: string;
  onSuccess: () => void;
}

export const useDraftContentView = ({
  draftName,
  draftAvatarId,
  draftAudioId,
  draftContent,
  draftId,
  onSuccess,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const textFieldRef = useRef<HTMLInputElement | null>(null);

  const { mutate: mutateSaveDraftContent } = useSaveDraftContent();

  const { setTextToSpeechContent } = useAppContext();

  const { push } = useRouter();

  const { enqueueSnackbar } = useSnackbar();

  const FormSchema = Yup.object().shape({
    content: Yup.string()
      .required('Content is required')
      .typeError('Content must be a valid string'),
  });

  const formMethods = useForm({
    resolver: yupResolver(FormSchema),
    defaultValues: { content: draftContent || '' },
    mode: 'onChange',
  });

  const { getValues, setValue } = formMethods;

  const handleCloseEdit = () => {
    setIsEditing(false);
  };

  const onToggleEdit = () => {
    setIsEditing((prevState) => !prevState);
  };

  const handleOnSaveDraft = () => {
    mutateSaveDraftContent(
      {
        videoName: draftName,
        avatarId: draftAvatarId,
        audioId: draftAudioId,
        text: getValues('content'),
        creationId: draftId,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Draft saved successfully!', { variant: 'success' });
          handleCloseEdit();
          onSuccess();
        },
        onError: (err: any) => {
          enqueueSnackbar(err?.message || 'Failed to save draft', { variant: 'error' });
        },
      }
    );
  };

  const handleOnUseInVideo = () => {
    push(`/create-video-details?draftVideoId=${draftId}`);
  };

  const handleOnBrowseAvatar = () => {
    push(`/create-video-details?draftVideoId=${draftId}&tab=${CreateVideoSideMenuItemEnum.AVATAR}`);
  };

  const handleOnUseInAudio = () => {
    push(`/text-to-audio`);
    setTextToSpeechContent(getValues('content'));
  };

  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    if (draftContent) {
      setValue('content', draftContent);
    }
  }, [draftContent]);

  return {
    formMethods,
    isEditing,
    handleOnSaveDraft,
    onToggleEdit,
    textFieldRef,
    handleOnUseInVideo,
    handleOnUseInAudio,
    handleOnBrowseAvatar
  };
};
